import ControlPanelController from "./control_panel_controller"
import Sortable from "sortablejs"

const KEBAB_SVG = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-gray-600"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"/></svg>'
const DRAG_SVG = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"/></svg>'
const PLUS_SVG = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3.5 h-3.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/></svg>'

export default class extends ControlPanelController {
  static targets = [
    // Stage list (client-rendered)
    "stageList",
    // Add step modal
    "addStepModal", "addStepName", "addStepTooltip", "addStepStageId", "addStepFilterTypes",
    "addFilterTable", "addFilterValidation", "addRulesValidation",
    "addStepNameValidation", "addStepTooltipValidation",
    "addRuleExisting", "addRuleFuture",
    // Edit step modal
    "editStepModal", "editStepTitle", "editStepId", "editStepStageId",
    "editStepName", "editStepTooltip", "editStepFilterTypes", "editStepFormType",
    "editFilterTable", "editFilterValidation", "editRulesValidation",
    "editStepNameValidation", "editStepTooltipValidation",
    "editRuleExisting", "editRuleExistingWithData", "editRuleFuture",
    // Delete step modal
    "deleteStepModal", "deleteStepTitle", "deleteStepId", "deleteStepCount", "deleteStepBtn",
    "deleteWithData", "deleteWithoutData",
    // Stage modals
    "stageModal", "stageModalTitle", "stageId", "stageName",
    "deleteStageModal", "deleteStageId",
    // Sortable containers
    "stepContainer"
  ]

  static values = {
    stagesUrl: String,
    stepTypesUrl: String,
    stepSequenceUrl: String,
    stagesAndStepsUrl: String,
    buildingTypes: Array
  }

  // ── Lifecycle ───────────────────────────────────────────────────────

  connect() {
    super.connect()
    this._onClickOutside = this._closeAllMenus.bind(this)
    document.addEventListener("click", this._onClickOutside)
    this._sortableInstances = []
    this.loadStages()
  }

  disconnect() {
    document.removeEventListener("click", this._onClickOutside)
    this._destroySortables()
  }

  // ── Load & Render ───────────────────────────────────────────────────

  async loadStages() {
    try {
      const response = await fetch(this.stagesAndStepsUrlValue, { headers: { Accept: "application/json" } })
      if (!response.ok) throw new Error("Failed to load stages")
      const stages = await response.json()
      this._renderStages(stages)
    } catch {
      this.stageListTarget.innerHTML = '<div class="text-center py-8 text-red-400 text-[13px]">Error loading stages</div>'
    }
  }

  _renderStages(stages) {
    if (!stages || stages.length === 0) {
      this.stageListTarget.innerHTML = '<div class="text-center py-8 text-gray-400 text-[13px]">No stages found</div>'
      return
    }

    this.stageListTarget.innerHTML = stages.map(stage => {
      const steps = stage.steps || []
      return `
        <div class="rounded-lg border border-gray-200">
          ${this._renderStageHeader(stage, steps)}
          <div class="divide-y divide-gray-200" data-cpanel-real-estate-target="stepContainer" data-stage-id="${stage.id}">
            ${steps.length > 0 ? this._renderColumnHeader() : ""}
            ${steps.map(step => this._renderStepRow(step, stage.id)).join("")}
            ${this._renderAddStepButton(stage.id)}
          </div>
        </div>`
    }).join("")

    this._initSortable()
  }

  _renderStageHeader(stage, steps) {
    const kebabMenu = stage.is_default ? "" : `
      <div class="relative">
        <button type="button" class="p-1 rounded hover:bg-gray-200 cursor-pointer"
                data-action="click->cpanel-real-estate#toggleMenu">
          ${KEBAB_SVG}
        </button>
        <div class="hidden absolute right-0 z-20 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg py-1 w-40">
          <button type="button" class="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                  data-action="click->cpanel-real-estate#editStage"
                  data-id="${stage.id}" data-name="${this._esc(stage.name)}">Rename</button>
          <button type="button" class="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                  data-action="click->cpanel-real-estate#deleteStage"
                  data-id="${stage.id}">Remove</button>
        </div>
      </div>`

    return `
      <div class="px-3 py-2 bg-gray-50 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <h3 class="text-[13px] font-semibold text-gray-500">${this._esc(stage.name)}</h3>
          <div class="flex items-center gap-3 text-[13px] text-text-muted">
            <span>${steps.length} steps</span>
            ${kebabMenu}
          </div>
        </div>
      </div>`
  }

  _renderColumnHeader() {
    return `
      <div class="flex items-center px-3 py-1.5 bg-gray-50/50 text-[13px] font-medium text-gray-400 tracking-wider">
        <div class="w-8"></div>
        <div class="flex-1 min-w-0 px-2">Name</div>
        <div class="w-[36rem] px-2 hidden lg:block">Tooltip</div>
        <div class="w-24 px-2 text-center hidden md:block">Form Type</div>
        <div class="w-36 px-2 text-center hidden md:block whitespace-nowrap">Property Types</div>
        <div class="w-10"></div>
      </div>`
  }

  _renderStepRow(step, stageId) {
    return `
      <div class="flex items-center px-3 py-1.5 bg-white hover:bg-gray-50 transition-colors step-row"
           data-step-id="${step.id}" data-stage-id="${stageId}">
        <div class="w-8 flex-shrink-0 drag-handle cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600">
          ${DRAG_SVG}
        </div>
        <div class="flex-1 min-w-0 px-2">
          <span class="text-[13px] font-medium text-text-body truncate block">${this._esc(step.name)}</span>
        </div>
        <div class="w-[36rem] px-2 hidden lg:block">
          <span class="text-[13px] text-text-muted truncate block">${this._esc(step.tooltip_description || "")}</span>
        </div>
        <div class="w-24 px-2 text-center hidden md:block">
          <span class="text-[13px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">${this._titleize(step.form_type || "")}</span>
        </div>
        <div class="w-36 px-2 text-center hidden md:block">
          <span class="text-[13px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded cursor-help"
                title="${this._esc(step.filter_types_summary || "")}">
            ${step.active_property_types_count}
          </span>
        </div>
        <div class="w-10 flex-shrink-0">
          <div class="relative">
            <button type="button" class="p-1 rounded hover:bg-gray-200 cursor-pointer"
                    data-action="click->cpanel-real-estate#toggleMenu">
              ${KEBAB_SVG}
            </button>
            <div class="hidden absolute right-0 z-20 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg py-1 w-40">
              <button type="button" class="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                      data-action="click->cpanel-real-estate#openEditStepModal"
                      data-id="${step.id}">Edit</button>
              <button type="button" class="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                      data-action="click->cpanel-real-estate#openDeleteStepModal"
                      data-id="${step.id}">Remove</button>
            </div>
          </div>
        </div>
      </div>`
  }

  _renderAddStepButton(stageId) {
    return `
      <div class="px-3 py-2 bg-white flex justify-end">
        <button type="button"
                class="text-[13px] text-brand hover:text-brand-dark font-medium cursor-pointer flex items-center gap-1"
                data-action="click->cpanel-real-estate#openAddStepModal"
                data-stage-id="${stageId}">
          ${PLUS_SVG}
          Add Step
        </button>
      </div>`
  }

  // ── Sortable ────────────────────────────────────────────────────────

  _destroySortables() {
    (this._sortableInstances || []).forEach(s => s.destroy())
    this._sortableInstances = []
  }

  _initSortable() {
    this._destroySortables()
    if (!this.hasStepContainerTarget) return

    this.stepContainerTargets.forEach(container => {
      const instance = Sortable.create(container, {
        group: "steps",
        animation: 150,
        handle: ".drag-handle",
        draggable: ".step-row",
        ghostClass: "opacity-30",
        onEnd: () => this._onSortEnd()
      })
      this._sortableInstances.push(instance)
    })
  }

  // ── Kebab menus ─────────────────────────────────────────────────────

  toggleMenu(event) {
    event.stopPropagation()
    const menu = event.currentTarget.nextElementSibling
    const wasHidden = menu.classList.contains("hidden")
    this._closeAllMenus()
    if (wasHidden) menu.classList.remove("hidden")
  }

  _closeAllMenus() {
    if (!this.hasStageListTarget) return
    this.stageListTarget.querySelectorAll(".relative > div.absolute").forEach(m => m.classList.add("hidden"))
  }

  // ── Helpers ─────────────────────────────────────────────────────────

  // Safe in BOTH text and attribute position — quotes must be escaped too,
  // or a value carrying an inch mark (`6" VERTICAL CURB`) closes its own
  // attribute and swallows the rest of the tag, data-* attributes included.
  _esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }

  _titleize(str) {
    if (!str) return ""
    return str.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
  }

  // ── Stages ─────────────────────────────────────────────────────────

  addStage() {
    this.stageIdTarget.value = ""
    this.stageNameTarget.value = ""
    this.stageModalTitleTarget.textContent = "Add Stage"
    this.stageModalTarget.classList.remove("hidden")
    this._lockScroll()
    this.stageNameTarget.focus()
  }

  editStage(event) {
    this._closeAllMenus()
    this.stageIdTarget.value = event.currentTarget.dataset.id
    this.stageNameTarget.value = event.currentTarget.dataset.name || ""
    this.stageModalTitleTarget.textContent = "Rename Stage"
    this.stageModalTarget.classList.remove("hidden")
    this._lockScroll()
    this.stageNameTarget.focus()
  }

  closeStageModal() {
    this.stageModalTarget.classList.add("hidden")
    this._unlockScroll()
  }

  async submitStage() {
    const name = this.stageNameTarget.value.trim()
    if (!name) return

    const id = this.stageIdTarget.value
    const isEdit = !!id

    try {
      const response = await fetch(isEdit ? `${this.stagesUrlValue}/${id}` : this.stagesUrlValue, {
        method: isEdit ? "PATCH" : "POST",
        headers: this._headers(),
        body: JSON.stringify({ real_estate_stage: { name } })
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this.closeStageModal()
        this._showToast(data.message || (isEdit ? "Updated" : "Stage added"))
        this.loadStages()
      } else {
        this._showToast(data.errors?.join(", ") || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  async deleteStage(event) {
    this._closeAllMenus()
    const id = event.currentTarget.dataset.id
    try {
      const checkResponse = await fetch(`${this.stagesUrlValue}/${id}/check_deletability`, {
        headers: this._headers()
      })
      const checkData = await checkResponse.json()
      if (!checkData.can_delete) {
        this._showToast(checkData.message || "Cannot delete", "error")
        return
      }
    } catch {
      // Continue anyway
    }

    this.deleteStageIdTarget.value = id
    this.deleteStageModalTarget.classList.remove("hidden")
    this._lockScroll()
  }

  closeDeleteStageModal() {
    this.deleteStageModalTarget.classList.add("hidden")
    this._unlockScroll()
  }

  async submitDeleteStage() {
    const id = this.deleteStageIdTarget.value
    try {
      const response = await fetch(`${this.stagesUrlValue}/${id}`, {
        method: "DELETE",
        headers: this._headers()
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this.closeDeleteStageModal()
        this._showToast(data.message || "Deleted")
        this.loadStages()
      } else {
        this._showToast(data.message || "Cannot delete", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Add Step ────────────────────────────────────────────────────────

  openAddStepModal(event) {
    const stageId = event.currentTarget.dataset.stageId
    this.addStepStageIdTarget.value = stageId

    // Reset form
    this.addStepNameTarget.value = ""
    this.addStepTooltipTarget.value = ""
    this.addRuleExistingTarget.checked = false
    this.addRuleFutureTarget.checked = false
    this.addStepNameValidationTarget.classList.add("hidden")
    this.addStepTooltipValidationTarget.classList.add("hidden")
    this.addFilterValidationTarget.classList.add("hidden")
    this.addRulesValidationTarget.classList.add("hidden")

    // Reset filter types checkboxes to all checked, clear custom names
    this.addFilterTableTarget.querySelectorAll('input[type="checkbox"]').forEach(cb => cb.checked = true)
    this.addFilterTableTarget.querySelectorAll('input[type="text"]').forEach(input => input.value = "")

    this._buildAddFilterTypes()
    this.addStepModalTarget.classList.remove("hidden")
    this._lockScroll()
  }

  closeAddStepModal() {
    this.addStepModalTarget.classList.add("hidden")
    this._unlockScroll()
  }

  updateAddFilterTypes() {
    this._buildAddFilterTypes()
  }

  _buildAddFilterTypes() {
    const filterTypes = this._collectFilterTypes(this.addFilterTableTarget)
    this.addStepFilterTypesTarget.value = JSON.stringify(filterTypes)
  }

  async submitCreateStep() {
    const name = this.addStepNameTarget.value.trim()
    const tooltip = this.addStepTooltipTarget.value.trim()

    // Validate name
    this.addStepNameValidationTarget.classList.toggle("hidden", !!name)
    // Validate tooltip
    this.addStepTooltipValidationTarget.classList.toggle("hidden", !!tooltip)

    if (!name || !tooltip) {
      if (!name) this.addStepNameTarget.focus()
      else this.addStepTooltipTarget.focus()
      return
    }

    // Validate filter types — at least one checkbox checked
    const filterTypes = JSON.parse(this.addStepFilterTypesTarget.value || "[]")
    const hasFilter = filterTypes.some(f => f.work_order_types && f.work_order_types.length > 0)
    if (!hasFilter) {
      this.addFilterValidationTarget.classList.remove("hidden")
      return
    }
    this.addFilterValidationTarget.classList.add("hidden")

    // Validate update rules
    const applyExisting = this.addRuleExistingTarget.checked
    const applyFuture = this.addRuleFutureTarget.checked
    if (!applyExisting && !applyFuture) {
      this.addRulesValidationTarget.classList.remove("hidden")
      return
    }
    this.addRulesValidationTarget.classList.add("hidden")

    const body = {
      real_estate_step_type: {
        name,
        tooltip_description: tooltip,
        real_estate_stage_id: this.addStepStageIdTarget.value,
        form_type: "standard",
        filter_types: this.addStepFilterTypesTarget.value,
        apply_to_existing_projects: applyExisting,
        apply_to_future_projects: applyFuture
      }
    }

    try {
      const response = await fetch(this.stepTypesUrlValue, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify(body)
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this.closeAddStepModal()
        this._showToast(data.message || "Step added")
        this.loadStages()
      } else {
        this._showToast(data.errors?.join(", ") || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Edit Step ───────────────────────────────────────────────────────

  async openEditStepModal(event) {
    this._closeAllMenus()
    const id = event.currentTarget.dataset.id

    try {
      const response = await fetch(`${this.stepTypesUrlValue}/${id}`, {
        headers: this._headers()
      })
      if (!response.ok) throw new Error("Failed to load step")
      const step = await response.json()

      this.editStepIdTarget.value = step.id
      this.editStepStageIdTarget.value = step.real_estate_stage_id
      this.editStepNameTarget.value = step.name || ""
      this.editStepTooltipTarget.value = step.tooltip_description || ""
      this.editStepTitleTarget.textContent = `Edit Step`

      // Set form type
      if (this.hasEditStepFormTypeTarget) {
        this.editStepFormTypeTarget.value = step.form_type || "standard"
      }

      // Reset update rules
      this.editRuleExistingTarget.checked = !!step.apply_to_existing_projects
      this.editRuleExistingWithDataTarget.checked = !!step.apply_to_existing_projects_with_data
      this.editRuleFutureTarget.checked = !!step.apply_to_future_projects

      // Populate filter types
      this._populateEditFilterTypes(step.filter_types || [])

      this.editStepNameValidationTarget.classList.add("hidden")
      this.editStepTooltipValidationTarget.classList.add("hidden")
      this.editFilterValidationTarget.classList.add("hidden")
      this.editRulesValidationTarget.classList.add("hidden")
      this.editStepModalTarget.classList.remove("hidden")
      this._lockScroll()
    } catch {
      this._showToast("Error loading step data", "error")
    }
  }

  _populateEditFilterTypes(filterTypesData) {
    // Reset all checkboxes and custom names
    this.editFilterTableTarget.querySelectorAll('input[type="checkbox"]').forEach(cb => cb.checked = false)
    this.editFilterTableTarget.querySelectorAll('input[type="text"]').forEach(input => input.value = "")

    // Populate from data
    filterTypesData.forEach(filter => {
      const propertyType = filter.property_type
      if (!propertyType) return

      const row = this.editFilterTableTarget.querySelector(`tr[data-property-type="${CSS.escape(propertyType)}"]`)
      if (!row) return

      const workOrderTypes = filter.work_order_types || []
      workOrderTypes.forEach(wot => {
        const cb = row.querySelector(`input[data-work-order-type="${wot}"]`)
        if (cb) cb.checked = true
      })

      const customInput = row.querySelector('input[data-field="custom-name"]')
      if (customInput && filter.custom_step_name) {
        customInput.value = filter.custom_step_name
      }
    })

    this._buildEditFilterTypes()
  }

  closeEditStepModal() {
    this.editStepModalTarget.classList.add("hidden")
    this._unlockScroll()
  }

  updateEditFilterTypes() {
    this._buildEditFilterTypes()
  }

  _buildEditFilterTypes() {
    const filterTypes = this._collectFilterTypes(this.editFilterTableTarget)
    this.editStepFilterTypesTarget.value = JSON.stringify(filterTypes)
  }

  async submitUpdateStep() {
    const name = this.editStepNameTarget.value.trim()
    const tooltip = this.editStepTooltipTarget.value.trim()
    const id = this.editStepIdTarget.value

    // Validate required fields
    this.editStepNameValidationTarget.classList.toggle("hidden", !!name)
    this.editStepTooltipValidationTarget.classList.toggle("hidden", !!tooltip)

    if (!name || !tooltip) {
      if (!name) this.editStepNameTarget.focus()
      else this.editStepTooltipTarget.focus()
      return
    }

    // Validate filter types
    const filterTypes = JSON.parse(this.editStepFilterTypesTarget.value || "[]")
    const hasFilter = filterTypes.some(f => f.work_order_types && f.work_order_types.length > 0)
    if (!hasFilter) {
      this.editFilterValidationTarget.classList.remove("hidden")
      return
    }
    this.editFilterValidationTarget.classList.add("hidden")

    // Validate update rules
    const applyExisting = this.editRuleExistingTarget.checked
    const applyExistingWithData = this.editRuleExistingWithDataTarget.checked
    const applyFuture = this.editRuleFutureTarget.checked
    if (!applyExisting && !applyExistingWithData && !applyFuture) {
      this.editRulesValidationTarget.classList.remove("hidden")
      return
    }
    this.editRulesValidationTarget.classList.add("hidden")

    const body = {
      real_estate_step_type: {
        name,
        tooltip_description: tooltip,
        real_estate_stage_id: this.editStepStageIdTarget.value,
        filter_types: this.editStepFilterTypesTarget.value,
        apply_to_existing_projects: applyExisting,
        apply_to_existing_projects_with_data: applyExistingWithData,
        apply_to_future_projects: applyFuture
      }
    }

    try {
      const response = await fetch(`${this.stepTypesUrlValue}/${id}`, {
        method: "PATCH",
        headers: this._headers(),
        body: JSON.stringify(body)
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this.closeEditStepModal()
        this._showToast(data.message || "Updated")
        this.loadStages()
      } else {
        this._showToast(data.errors?.join(", ") || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Delete Step ─────────────────────────────────────────────────────

  async openDeleteStepModal(event) {
    this._closeAllMenus()
    const id = event.currentTarget.dataset.id

    try {
      const response = await fetch(`${this.stepTypesUrlValue}/${id}`, {
        headers: this._headers()
      })
      if (!response.ok) throw new Error("Failed to load step")
      const step = await response.json()

      this.deleteStepIdTarget.value = step.id
      this.deleteStepTitleTarget.textContent = `Delete ${step.name}`
      this.deleteStepCountTarget.textContent = `(${step.projects_with_data_count || 0} projects have data in this step)`

      // Reset checkboxes
      this.deleteWithDataTarget.checked = false
      this.deleteWithoutDataTarget.checked = false
      this.deleteStepBtnTarget.disabled = true

      this.deleteStepModalTarget.classList.remove("hidden")
      this._lockScroll()
    } catch {
      this._showToast("Error loading step data", "error")
    }
  }

  closeDeleteStepModal() {
    this.deleteStepModalTarget.classList.add("hidden")
    this._unlockScroll()
  }

  toggleDeleteButton() {
    const hasSelection = this.deleteWithDataTarget.checked || this.deleteWithoutDataTarget.checked
    this.deleteStepBtnTarget.disabled = !hasSelection
  }

  async submitDeleteStep() {
    const id = this.deleteStepIdTarget.value
    const body = {
      delete_for_existing_with_data: this.deleteWithDataTarget.checked ? "true" : "false",
      delete_for_existing_without_data: this.deleteWithoutDataTarget.checked ? "true" : "false",
      delete_for_all_future_projects: "true"
    }

    try {
      const response = await fetch(`${this.stepTypesUrlValue}/${id}`, {
        method: "DELETE",
        headers: this._headers(),
        body: JSON.stringify(body)
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this.closeDeleteStepModal()
        this._showToast(data.message || "Deleted")
        this.loadStages()
      } else {
        this._showToast(data.error || "Cannot delete", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Drag & Drop ─────────────────────────────────────────────────────

  async _onSortEnd() {
    const sequences = []

    this.stepContainerTargets.forEach(container => {
      const stageId = container.dataset.stageId
      const rows = container.querySelectorAll(".step-row")
      rows.forEach((row, index) => {
        sequences.push([
          parseInt(row.dataset.stepId),
          index + 1,
          parseInt(stageId)
        ])
      })
    })

    if (sequences.length === 0) return

    try {
      const response = await fetch(this.stepSequenceUrlValue, {
        method: "PUT",
        headers: this._headers(),
        body: JSON.stringify({ new_sequence: JSON.stringify(sequences) })
      })
      if (response.ok) {
        this._showToast("Step order updated")
      } else {
        this._showToast("Error saving order", "error")
        this.loadStages()
      }
    } catch {
      this._showToast("Error saving order", "error")
      this.loadStages()
    }
  }

  // ── Shared Filter Types Helper ──────────────────────────────────────

  _collectFilterTypes(tableBody) {
    const filterTypes = []
    const rows = tableBody.querySelectorAll("tr[data-property-type]")

    rows.forEach(row => {
      const propertyType = row.dataset.propertyType
      const workOrderTypes = []

      row.querySelectorAll('input[type="checkbox"]').forEach(cb => {
        if (cb.checked && cb.dataset.workOrderType) {
          workOrderTypes.push(cb.dataset.workOrderType)
        }
      })

      const customInput = row.querySelector('input[data-field="custom-name"]')
      const customStepName = customInput ? customInput.value.trim() : ""

      filterTypes.push({
        property_type: propertyType,
        work_order_types: workOrderTypes,
        custom_step_name: customStepName
      })
    })

    return filterTypes
  }

  // ── Labels (kept from original) ─────────────────────────────────────

  async saveLabels(event) {
    event.preventDefault()
    const form = event.currentTarget
    const formData = new FormData(form)
    const labels = {}
    for (const [key, value] of formData.entries()) {
      if (key.startsWith("labels[")) {
        const label = key.replace("labels[", "").replace("]", "")
        labels[label] = value
      }
    }

    try {
      const url = window.location.pathname.replace(/\/real_estate\/.*$/, "/real_estate/update_labels")
      const response = await fetch(url, {
        method: "PATCH",
        headers: this._headers(),
        body: JSON.stringify({ labels })
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this._showToast(data.message || "Saved")
      } else {
        this._showToast(data.errors?.join(", ") || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }
}
